'use client';

import Image from 'next/image';

import { cn } from '@/shared/lib/utils';

interface GalleryItem {
  title: string;
  art: string;
  image?: string;
  image_alt?: string;
}

interface GalleryProps {
  id?: string;
  label?: string;
  title?: string;
  description?: string;
  items?: GalleryItem[];
  className?: string;
}

export function Gallery({ id, label, title, description, items, className }: GalleryProps) {
  if (!items || items.length === 0) return null;

  // Duplicate items for infinite scroll effect
  const scrollItems = [...items, ...items];

  return (
    <section id={id} className={cn('section-warm overflow-hidden py-16 md:py-24', className)}>
      {/* Section Header */}
      <div className="container">
        {(title || description) && (
          <div className="section-header mb-12 max-w-[600px]">
            {label && (
              <p className="section-label">{label}</p>
            )}
            {title && (
              <h2 className="section-title text-foreground">{title}</h2>
            )}
            {description && (
              <p className="text-lg font-light text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Infinite Scrolling Gallery */}
      <div className="gallery-scroll-container">
        {scrollItems.map((item, index) => (
          <div
            key={index}
            className="gallery-item-image"
          >
            {/* Image Display */}
            {item.image ? (
              <Image
                src={item.image}
                alt={item.image_alt || item.title}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 70vw, 280px"
                quality={60}
                unoptimized
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-card">
                <span className="text-4xl opacity-30">🧵</span>
              </div>
            )}
            
            {/* Title Overlay */}
            <span className="absolute bottom-4 left-0 right-0 text-center font-display text-xs font-medium uppercase tracking-widest text-foreground/80">
              {item.title}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
